import React, { useState } from 'react'
import { useRouter } from 'next/router'
import { useQueryClient } from 'react-query'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast'
import { Spinner } from '@wartek-id/spinner'
import { Icon } from '@wartek-id/icon-v2'
import { historyUtils } from '@wartek-id/toolbox-helper'

import AndroidHandler from 'utils/androidHandler'
import {
  API_PATH_GET_KS_SELECTION_PROPOSAL,
  API_PATH_GET_KS_SELECTION_CANDIDATE,
  useMutateAPIKSProposal,
  useMutateAPIKSSelfProposal,
  useQueryAPIGetKSSelectionProposal,
  useQueryAPIGetKSSelectionCandidate,
} from 'api/bcks/useAPIBCKSInvitation'
import Header from 'app/BCKSPage/components/Header'
import { useBCKSDesktopView } from 'app/BCKSPage/hooks/useBCKSDesktopView'
import { BCKSDesktopPageShell } from 'app/BCKSPage/components/BCKSDesktopPageShell'
import { BCKSProposalConfirmation } from 'app/BCKSPage/components/BCKSProposalConfirmation'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/Table'
import { SelectionStepsV2 } from '../components/SelectionSteps/SelectionStepsV2'
import { useAuth } from 'utils/auth'
import { useAPIGetKepalaSekolahEligibility } from 'api/bcks/useAPIBCKSQualification'

import type {
  CandidateItem,
  ResponseGetKSSelectionProposal,
  ResponseSuccessKSSelectionCandidate,
} from 'api/bcks/useAPIBCKSInvitation'

export const KSProposalPage = () => {
  const router = useRouter()

  const { data: candidateData } = useQueryAPIGetKSSelectionCandidate()

  // --------------------------------------
  // Get proposal data and its derivations
  // --------------------------------------
  const { data: proposalData, isLoading } = useQueryAPIGetKSSelectionProposal(
    {
      staleTime: 60 * 60 * 1000, // Stale after 60min, force to refetch upon mounting
      useErrorBoundary: false, // Prevent whole page error
    },
    { version: 2 }
  )
  const candidates = proposalData?.candidates || []
  const hasCandidateToPropose = candidates.length > 0
  const isSubmitted =
    proposalData?.proposalStatus === 'selected' ||
    (candidateData &&
      candidateData.selectionCandidateState === 'documentSubmission')

  // ---------------------------------
  // Handle navigation and redirection
  // ---------------------------------
  const androidHandler = AndroidHandler()
  const handleClickBack = () => {
    if (androidHandler.enabled) historyUtils.backToApp(router.back)
    else router.back()
  }

  React.useEffect(() => {
    if (!isLoading && !hasCandidateToPropose) {
      handleClickBack()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCandidateToPropose, isLoading])

  const { isDesktop } = useBCKSDesktopView()

  if (isLoading || !hasCandidateToPropose) {
    if (isDesktop) {
      return (
        <BCKSDesktopPageShell title="Usulkan Guru" pageLabel="Usulkan Guru">
          <main className="flex flex-col w-full h-64 items-center justify-center">
            <Spinner size="lg" />
            <Text className="mt-4" variant="body">
              Halaman sedang dimuat...
            </Text>
          </main>
        </BCKSDesktopPageShell>
      )
    }

    return (
      <div className="relative flex flex-col h-screen max-w-xl mx-auto items-center">
        <Header
          title="Usulkan Guru"
          variant="large"
          withRightActionButton={false}
          onBackClick={handleClickBack}
        />
        <main className="flex flex-col content-around sticky w-full h-full p-2 items-center justify-center">
          <Spinner size="lg" />
          <Text className="mt-4" variant="body">
            Halaman sedang dimuat...
          </Text>
        </main>
      </div>
    )
  }

  if (isDesktop) {
    return (
      <BCKSDesktopPageShell
        title="Usulkan Guru di Sekolah Anda"
        pageLabel="Usulkan Guru di Sekolah Anda"
      >
        <main>
          {isSubmitted ? (
            <SectionSuccessProposal
              candidateData={candidateData}
              proposalData={proposalData}
            />
          ) : (
            <SectionSelectCandidates
              candidates={candidates}
              selectionId={proposalData?.selectionId}
            />
          )}

          <section>
            <div className="p-4">
              <Text variant="heading-sm" as="h2">
                Berikut seluruh tahapan yang akan dilalui Guru Anda jika
                mengikuti seleksi:
              </Text>
            </div>
            <div>
              <SelectionStepsV2 staticView />
            </div>
          </section>
        </main>
      </BCKSDesktopPageShell>
    )
  }

  return (
    <div className="relative flex flex-col h-screen max-w-xl mx-auto">
      <Header
        isSticky
        title="Usulkan Guru di Sekolah Anda"
        variant="large"
        withRightActionButton={false}
        onBackClick={handleClickBack}
      />
      <main className="sticky">
        {isSubmitted ? (
          <SectionSuccessProposal
            candidateData={candidateData}
            proposalData={proposalData}
          />
        ) : (
          <SectionSelectCandidates
            candidates={candidates}
            selectionId={proposalData?.selectionId}
          />
        )}

        <section>
          <div className="p-4">
            <Text variant="heading-sm" as="h2">
              Berikut seluruh tahapan yang akan dilalui Guru Anda jika mengikuti
              seleksi:
            </Text>
          </div>
          <div>
            <SelectionStepsV2 staticView />
          </div>
        </section>
      </main>
    </div>
  )
}

const MAX_SELECTION_COUNT = 2

type SectionSelectCandidatesProps = {
  selectionId: string
  candidates: Array<CandidateItem>
}

export const SectionSelectCandidates = ({
  selectionId,
  candidates,
}: SectionSelectCandidatesProps) => {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { user } = session

  // -------------------------
  // Handle candidate toggle
  // -------------------------
  const [selectedIds, setSelectedIds] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasSelectedIds = selectedIds.length > 0
  const handleToggle = (candidateId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedIds((ids) => [...ids, candidateId])
    } else {
      setSelectedIds((ids) => ids.filter((id) => id != candidateId))
    }
  }

  const ksUser = candidates.find((c) => c.candidateId === user.id)
  const candidateData = ksUser
    ? candidates.filter((c) => c.candidateId !== user.id)
    : candidates

  const isKSSelected = selectedIds.includes(user.id)

  // -------------------------
  // Handle confirmation sheet
  // -------------------------
  const [showSheet, setShowSheet] = useState(false)
  const handleClose = () => {
    setShowSheet(false)
  }

  // -----------------
  // Handle submission
  // -----------------
  const proposalMutation = useMutateAPIKSProposal(selectionId)
  const selfProposalMutation = useMutateAPIKSSelfProposal(selectionId)

  const handleSubmit = async () => {
    // Do nothing if there is any current submission or has no selected candidate
    if (
      proposalMutation.isLoading ||
      selfProposalMutation.isLoading ||
      !hasSelectedIds
    )
      return

    setIsSubmitting(true)

    try {
      if (isKSSelected) {
        await selfProposalMutation.mutateAsync({})
      }

      const candidateIdsToPropose = selectedIds.filter((id) => id !== user.id)
      if (candidateIdsToPropose.length > 0) {
        await proposalMutation.mutateAsync({
          candidateIds: candidateIdsToPropose,
        })
      }
      // Invalidate cache of GET query
      queryClient.invalidateQueries(API_PATH_GET_KS_SELECTION_CANDIDATE)
      queryClient.invalidateQueries(API_PATH_GET_KS_SELECTION_PROPOSAL)
    } catch (e) {
      // Display error message
      toast({
        message: `Gagal mengusulkan guru: ${e.originalError?.response?.data?.error?.message}`,
      })
    } finally {
      // Dismiss the sheet
      setShowSheet(false)
      setIsSubmitting(false)
    }
  }

  return (
    <section className="p-4 space-y-6">
      <Text as="h2" variant="heading-sm">
        Pilih Guru terbaik yang memenuhi persyaratan BCKS (Pengusulan diri
        sendiri atau Guru lain di sekolah Anda)
      </Text>

      <Text variant="body">
        Proses pengusulan Guru untuk mengikuti seleksi Kepala Sekolah bersifat{' '}
        <strong>tidak wajib</strong>. Anda dapat melewati proses ini apabila
        belum memiliki usulan atau memilih untuk tidak mengusulkan Guru saat
        ini.
      </Text>

      <div className="space-y-3">
        {ksUser && (
          <Table withOuterBorder rounded>
            <TableHeader>
              <TableHeaderItem>
                <Text variant="action" as="span">
                  Nama Kepala Sekolah
                </Text>
              </TableHeaderItem>
              <TableHeaderItem>
                <span className="sr-only">Aksi</span>
              </TableHeaderItem>
            </TableHeader>
            <TableBody>
              <TableRow
                className={clsx(isKSSelected && 'bg-surface-selected-default')}
              >
                <TableRowItem className="align-middle">
                  <div className="flex items-center gap-2">
                    {isKSSelected && (
                      <Icon color="informational">check_circle</Icon>
                    )}
                    <Text
                      variant="body-sm"
                      as="span"
                      className="overflow-wrap-anywhere"
                    >
                      {ksUser.name}
                    </Text>
                  </div>
                </TableRowItem>
                <TableRowItem>
                  <Button
                    size="sm"
                    color={isKSSelected ? 'white' : 'black'}
                    className="min-w-14 ml-auto"
                    role="checkbox"
                    aria-checked={isKSSelected}
                    aria-label={user.name}
                    disabled={
                      !isKSSelected && selectedIds.length >= MAX_SELECTION_COUNT
                    }
                    onClick={() => handleToggle(user.id, !isKSSelected)}
                  >
                    {isKSSelected ? 'Dipilih' : 'Pilih'}
                  </Button>
                </TableRowItem>
              </TableRow>
            </TableBody>
          </Table>
        )}

        {candidateData && candidateData.length > 0 && (
          <Table withOuterBorder rounded>
            <TableHeader>
              <TableHeaderItem>
                <Text variant="action" as="span">
                  Nama Lengkap Guru
                </Text>
              </TableHeaderItem>
              <TableHeaderItem>
                <span className="sr-only">Aksi</span>
              </TableHeaderItem>
            </TableHeader>
            <TableBody>
              {candidateData.map((candidate) => {
                const isSelected = selectedIds.some(
                  (id) => id === candidate.candidateId
                )

                return (
                  <TableRow
                    key={candidate.candidateId}
                    className={clsx(
                      isSelected && 'bg-surface-selected-default'
                    )}
                  >
                    <TableRowItem className="align-middle">
                      <div className="flex items-center gap-2">
                        {isSelected && (
                          <Icon color="informational">check_circle</Icon>
                        )}
                        <Text
                          variant="body-sm"
                          as="span"
                          className="overflow-wrap-anywhere"
                        >
                          {candidate.name}
                        </Text>
                      </div>
                    </TableRowItem>
                    <TableRowItem>
                      <Button
                        size="sm"
                        color={isSelected ? 'white' : 'black'}
                        className="min-w-14 ml-auto"
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={candidate.name}
                        disabled={
                          !isSelected &&
                          selectedIds.length >= MAX_SELECTION_COUNT
                        }
                        onClick={() =>
                          handleToggle(candidate.candidateId, !isSelected)
                        }
                      >
                        {isSelected ? 'Dipilih' : 'Pilih'}
                      </Button>
                    </TableRowItem>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}

        <Button
          size="lg"
          onClick={() => setShowSheet(true)}
          type="button"
          disabled={!hasSelectedIds}
          fullWidth
        >
          Kirim usulan
        </Button>

        <BCKSProposalConfirmation
          isOpen={showSheet}
          isLoading={isSubmitting}
          onClose={handleClose}
          onConfirm={handleSubmit}
          title="Konfirmasi Pengusulan Guru"
          description={
            <>
              Dengan memilih <strong>Saya setuju</strong>, berarti Guru yang
              Anda pilih akan menerima undangan dan dapat mengikuti seluruh
              tahapan seleksi Kepala Sekolah.
            </>
          }
        />
      </div>
    </section>
  )
}

type SectionSuccessProposalProps = {
  candidateData: ResponseSuccessKSSelectionCandidate
  proposalData: ResponseGetKSSelectionProposal
}

function SectionSuccessProposal({
  candidateData,
  proposalData,
}: SectionSuccessProposalProps) {
  const isDocumentSubmission =
    candidateData?.selectionCandidateState === 'documentSubmission'
  const candidates = proposalData?.candidates || []

  const { data: profile } = useAPIGetKepalaSekolahEligibility({
    enabled: isDocumentSubmission,
  })

  return (
    <section className="px-4 py-6">
      <div className="p-3 rounded bg-surface-success-subdued">
        <Icon className="mb-3" color="success">
          check_circle
        </Icon>

        {isDocumentSubmission && (
          <div className="mb-4">
            <Text as="h2" className="mb-4">
              Berhasil usulkan Kepala Sekolah berikut:
            </Text>

            <div>
              <div className="flex items-start space-x-1">
                <Text as="dt" className="w-12 flex-shrink-0">
                  Nama
                </Text>
                <Text as="span">:</Text>
                <Text as="dd" className="overflow-wrap-anywhere">
                  {profile?.identity.nama.value}
                </Text>
              </div>

              <div className="flex items-start space-x-1">
                <Text as="dt" className="w-12 flex-shrink-0">
                  NIP
                </Text>
                <Text as="span">:</Text>
                <Text as="dd" className="overflow-wrap-anywhere">
                  {profile?.identity.nip.value}
                </Text>
              </div>
            </div>
          </div>
        )}

        {proposalData.proposalStatus === 'selected' && candidates.length > 0 && (
          <div>
            <Text as="h2" className="mb-4">
              Berhasil usulkan Guru berikut:
            </Text>

            <ul>
              {candidates.map((candidate) => {
                return (
                  <li key={candidate.candidateId}>
                    <dl>
                      <div className="flex items-start space-x-1">
                        <Text as="dt" className="w-12 flex-shrink-0">
                          Nama
                        </Text>
                        <Text as="span">:</Text>
                        <Text as="dd" className="overflow-wrap-anywhere">
                          {candidate.name}
                        </Text>
                      </div>

                      <div className="flex items-start space-x-1">
                        <Text as="dt" className="w-12 flex-shrink-0">
                          NIP
                        </Text>
                        <Text as="span">:</Text>
                        <Text as="dd" className="overflow-wrap-anywhere">
                          {candidate.nip}
                        </Text>
                      </div>
                    </dl>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}
